import React, { useEffect, useState } from "react";
import {
    IonButton,
    IonCard,
    IonContent,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonAlert,
    IonThumbnail,
    IonImg,
} from "@ionic/react";
import { banOutline, camera, trash } from "ionicons/icons";
import { Camera, CameraResultType, CameraSource, CameraDirection } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { PermitAffectedService } from "../models/PermitAffectedService";
import { SitePhoto } from "../models/SitePhoto";
import database from "../Database";
import EditSitePhotoModal from "./EditSitePhotoModal";
import "./AffectedServicePhotos.css";
import { SitePhotoType } from "../models/SitePhotoType";

interface Props {
    affectedServices: PermitAffectedService[];
    sitePhotos?: SitePhoto[];
    permitId: string;
    isReadOnly: boolean;
    ableToAddPhotos: boolean;
    editablePhotoTypes: SitePhotoType[];
    onAffectedServicePhotoCaptured: (dataUrl: string, affectedService: PermitAffectedService, comments: string) => void;
    onAffectedServiceNoPhotoReason: (affectedService: PermitAffectedService, reason: string) => void;
    onSitePhotoDeleted: (sitePhotoId: string) => void;
}

interface AffectedServicePhotoInfo {
    service: PermitAffectedService;
    sitePhoto?: SitePhoto;
    filePath?: string;
}

const AffectedServicePhotos: React.FC<Props> = ({
    affectedServices,
    sitePhotos,
    permitId,
    isReadOnly,
    ableToAddPhotos,
    editablePhotoTypes,
    onAffectedServicePhotoCaptured,
    onAffectedServiceNoPhotoReason,
    onSitePhotoDeleted,
}) => {
    const [serviceInfos, setServiceInfos] = useState<AffectedServicePhotoInfo[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedPhotoPath, setSelectedPhotoPath] = useState("");
    const [showReasonAlert, setShowReasonAlert] = useState(false);
    const [selectedService, setSelectedService] = useState<PermitAffectedService | null>(null);
    const [alertKey, setAlertKey] = useState(0);
    const [showEditModal, setShowEditModal] = useState(false);
    const [capturedPhotoPath, setCapturedPhotoPath] = useState("");

    useEffect(() => {
        const infos: AffectedServicePhotoInfo[] = [];
        const filePromises: Array<Promise<void>> = [];

        affectedServices.forEach((service) => {
            const photo = sitePhotos?.find((sp) => sp.affectedService?.name === service.name && !sp.pendingDelete);
            const info: AffectedServicePhotoInfo = { service, sitePhoto: photo };

            if (photo && photo.fileKey) {
                filePromises.push(
                    database.loadPermitFile(permitId, photo.fileKey).then(
                        (result: any) => {
                            if (result) {
                                info.filePath = Capacitor.convertFileSrc(result);
                            }
                        },
                        (err) => {
                            // ignore load errors
                        }
                    )
                );
            }
            infos.push(info);
        });

        Promise.all(filePromises).then(() => {
            setServiceInfos([...infos]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sitePhotos, affectedServices]);

    const takePicture = async (affectedService: PermitAffectedService) => {
        try {
            const image = await Camera.getPhoto({
                resultType: CameraResultType.DataUrl,
                source: CameraSource.Camera,
                direction: CameraDirection.Rear,
                quality: 80,
            });

            if (image.dataUrl) {
                setSelectedService(affectedService);
                setCapturedPhotoPath(image.dataUrl);
                setShowEditModal(true);
            }
        } catch (error) {
            console.error("Error picking photo:", error);
        }
    };

    const handleEditModalSubmit = (comments: string) => {
        if (selectedService && capturedPhotoPath) {
            onAffectedServicePhotoCaptured(capturedPhotoPath, selectedService, comments);
        }
        setShowEditModal(false);
        setCapturedPhotoPath("");
        setSelectedService(null);
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
        setCapturedPhotoPath("");
        setSelectedService(null);
    };

    const handleReasonButtonClick = (service: PermitAffectedService) => {
        setSelectedService(service);
        setShowReasonAlert(true);
    };

    const handleReasonSubmit = (reason: string) => {
        if (selectedService && reason && reason.trim()) {
            onAffectedServiceNoPhotoReason(selectedService, reason.trim());
        }
        setShowReasonAlert(false);
        setSelectedService(null);
    };

    const handlePhotoClick = (filePath: string) => {
        setSelectedPhotoPath(filePath);
        setShowModal(true);
    };

    if (!affectedServices || affectedServices.length === 0) {
        return (
            <p>
                <IonLabel style={{ marginLeft: "16px" }} class="ion-text-wrap">
                    No affected services
                </IonLabel>
            </p>
        );
    }

    const renderServiceItem = (info: AffectedServicePhotoInfo) => {
        const { service, sitePhoto, filePath } = info;
        const hasPhoto = sitePhoto && sitePhoto.fileKey;
        const hasReason = sitePhoto && sitePhoto.noPhotoReason;

        if (hasPhoto) {
            return (
                <IonItem key={service.name} onClick={() => filePath && handlePhotoClick(filePath)}>
                    {!isReadOnly && (
                        <div slot="start">
                            <div className="done-indicator"></div>
                        </div>
                    )}
                    {filePath && (
                        <IonThumbnail slot="start">
                            <IonImg src={filePath} />
                        </IonThumbnail>
                    )}
                    <IonLabel>
                        <h3>{service.name}</h3>
                        {sitePhoto!.comments && <p>{sitePhoto!.comments}</p>}
                    </IonLabel>
                    {!isReadOnly && editablePhotoTypes.some((type) => type.key === sitePhoto!.type.key) && (
                        <IonButton
                            size="default"
                            slot="end"
                            fill="clear"
                            color="medium"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSitePhotoDeleted(sitePhoto!.id);
                            }}
                        >
                            <IonIcon slot="icon-only" icon={trash}></IonIcon>
                        </IonButton>
                    )}
                </IonItem>
            );
        }

        if (hasReason) {
            return (
                <IonItem key={service.name}>
                    {!isReadOnly && (
                        <div slot="start">
                            <div className="done-indicator"></div>
                        </div>
                    )}
                    <IonThumbnail slot="start">
                        <IonButton
                            fill="outline"
                            color="medium"
                            expand="block"
                            style={{ margin: "0px", padding: "0px" }}
                        >
                            <IonIcon
                                color="medium"
                                size="large"
                                icon={banOutline}
                                style={{ margin: "0px", padding: "0px" }}
                            ></IonIcon>
                        </IonButton>
                    </IonThumbnail>
                    <IonLabel>
                        <h3>{service.name}</h3>
                        <p>Reason: {sitePhoto!.noPhotoReason}</p>
                    </IonLabel>
                    {!isReadOnly && editablePhotoTypes.some((type) => type.key === sitePhoto!.type.key) && (
                        <IonButton
                            size="default"
                            slot="end"
                            fill="clear"
                            color="medium"
                            onClick={(e) => {
                                e.stopPropagation();
                                onSitePhotoDeleted(sitePhoto!.id);
                            }}
                        >
                            <IonIcon slot="icon-only" icon={trash}></IonIcon>
                        </IonButton>
                    )}
                </IonItem>
            );
        }

        // No photo and no reason yet
        return (
            <IonItem key={service.name}>
                {!isReadOnly && (
                    <div slot="start">
                        <div className="notdone-indicator"></div>
                    </div>
                )}
                <IonThumbnail slot="start">
                    <IonButton
                        color="primary"
                        fill="outline"
                        expand="block"
                        onClick={() => takePicture(service)}
                        disabled={isReadOnly || !ableToAddPhotos}
                        style={{ margin: "0px", padding: "0px" }}
                    >
                        <IonIcon
                            color="primary"
                            size="large"
                            icon={camera}
                            style={{ margin: "0px", padding: "0px" }}
                        ></IonIcon>
                    </IonButton>
                </IonThumbnail>
                <IonLabel>
                    <h3>{service.name}</h3>
                    {!isReadOnly && (
                        <IonButton
                            shape="round"
                            fill="outline"
                            size="small"
                            color="primary"
                            disabled={isReadOnly || !ableToAddPhotos}
                            onClick={() => handleReasonButtonClick(service)}
                        >
                            Reason for no photo
                        </IonButton>
                    )}
                </IonLabel>
            </IonItem>
        );
    };

    return (
        <>
            <IonModal isOpen={showModal}>
                <IonContent>
                    <IonCard>
                        <img alt="SitePhoto" src={selectedPhotoPath} />
                    </IonCard>
                </IonContent>
                <IonButton onClick={() => setShowModal(false)}>Close</IonButton>
            </IonModal>

            <EditSitePhotoModal
                sitePhotoPath={capturedPhotoPath}
                open={showEditModal}
                onSubmit={handleEditModalSubmit}
                onClose={handleEditModalClose}
            />

            <IonAlert
                key={alertKey}
                isOpen={showReasonAlert}
                header="Reason for no photo"
                inputs={[
                    {
                        name: "reason",
                        type: "textarea",
                        placeholder: "Enter reason...",
                        value: "",
                    },
                ]}
                buttons={[
                    {
                        text: "Cancel",
                        role: "cancel",
                    },
                    {
                        text: "Submit Reason",
                        role: "confirm",
                        handler: (data: { reason: string }) => {
                            handleReasonSubmit(data.reason);
                        },
                    },
                ]}
                onDidDismiss={() => {
                    setShowReasonAlert(false);
                    setSelectedService(null);
                    setAlertKey((k) => k + 1);
                }}
            />

            <IonList inset style={{ margin: "0px" }}>
                {serviceInfos.map(renderServiceItem)}
            </IonList>
        </>
    );
};

export default AffectedServicePhotos;
